/**
 * ENG-7492: durable, on-disk record of the bare-named global/shared skill
 * folders the manager planted in an agent's `.claude/skills/`, so the orphan
 * sweep survives a manager restart.
 *
 * Global + shared skills (ADR-0023 / ENG-6349 / ENG-6815) are written as
 * `.claude/skills/<code_name>/SKILL.md` with NO folder prefix (so the agent's
 * `/<code_name>` slash-command resolves). The integration orphan sweep keys off
 * the `integration-`/`plugin-` prefixes and therefore cannot see them, so the
 * manager historically tracked the delivered set only in memory
 * (`agentState.knownGlobalSkillIds`). That set resets to empty on every manager
 * restart, so a skill unassigned while the manager was down — or unassigned
 * after any restart — was never pruned from disk: the agent kept auto-loading a
 * skill the operator had uninstalled (a governance risk when a skill is pulled
 * for cause).
 *
 * This manifest persists the delivered id set so the sweep becomes a true
 * reconcile-to-desired-state: on each refresh the manager prunes any manifest id
 * that is no longer in the desired (currently-assigned) set, then rewrites the
 * manifest to the new desired set. Only ids recorded here are ever eligible for
 * deletion, so the sweep never touches the agent's own skills (kanban,
 * core-knowledge), integration skills, or any user-created folder.
 */
import { existsSync, mkdirSync, readFileSync, writeFileSync } from 'node:fs';
import { dirname, join } from 'node:path';

const MANIFEST_VERSION = 1 as const;

interface ManagedSkillManifestFile {
  version: number;
  /** Bare skill ids (kebab code_names) delivered as global/shared skills. */
  globalSkillIds: string[];
}

/**
 * Absolute path to an agent's managed-skill manifest.
 *
 * `agentRootDir` is the agent's `~/.augmented/<codeName>` runtime root (NOT the
 * `provision/` subdir and NOT the `skills/` dir) — a location that persists
 * across manager restarts but is rebuilt on a full re-provision. Keeping the
 * manifest a sibling of `skills/` (never inside it) guarantees it can't be
 * mistaken for a skill folder by any sweep.
 */
export function managedSkillManifestPath(agentRootDir: string): string {
  return join(agentRootDir, 'managed-skills.json');
}

/**
 * Read the persisted set of manager-delivered global/shared skill ids.
 *
 * Fail-soft by design: a missing, unreadable, corrupt, or legacy-shaped file
 * returns an empty set. An empty prev set can only ever REDUCE what the sweep
 * deletes (fewer known ids → fewer candidates), so a bad manifest can never
 * cause an over-deletion.
 */
export function readManagedSkillManifest(path: string): Set<string> {
  try {
    if (!existsSync(path)) return new Set();
    const parsed = JSON.parse(readFileSync(path, 'utf-8')) as Partial<ManagedSkillManifestFile>;
    const ids = Array.isArray(parsed?.globalSkillIds) ? parsed.globalSkillIds : [];
    return new Set(ids.filter((id): id is string => typeof id === 'string' && id.length > 0));
  } catch {
    return new Set();
  }
}

/**
 * Persist the current manager-delivered global/shared skill id set.
 *
 * Best-effort — a write failure is non-fatal: the in-memory delivered set still
 * drives the current cycle, and the next refresh retries the write. Ids are
 * sorted so the file is stable/diff-friendly across refreshes.
 */
export function writeManagedSkillManifest(path: string, ids: Set<string>): void {
  try {
    mkdirSync(dirname(path), { recursive: true });
    const body: ManagedSkillManifestFile = {
      version: MANIFEST_VERSION,
      globalSkillIds: [...ids].sort(),
    };
    writeFileSync(path, JSON.stringify(body, null, 2));
  } catch {
    /* non-fatal: the manifest is a restart-durability optimization, not a gate */
  }
}
